import { useEffect, useRef, useState } from 'react'
import {
  ACCESS_GATE_SECONDARY_PATHS,
  CUIDARI_ACCESS_GATE_COPY,
  buildWaitlistPrimaryPath,
} from '@francisquiniai/cuidari-core/auth'
import { liteSession } from '@/lib/waitlist/lite-session'
import { getMe, type WaitlistMe } from '@/lib/waitlist/peitho-client'
import { BrandLockup, ReleaseTeaser } from './brand'
import { AccessPathCard, SecondaryPathButton } from './gateUi'
import { InviteForm } from './InviteForm'
import { LoginForm } from './LoginForm'
import { icon, type View } from './shared'
import { WaitlistForm } from './WaitlistForm'
import { WaitlistStatusCard } from './WaitlistStatusCard'
import '../login.css'

function GateHome({
  waitlistTokenSeen,
  setView,
}: {
  waitlistTokenSeen: boolean
  setView: (view: View) => void
}) {
  const gateCopy = CUIDARI_ACCESS_GATE_COPY.gate
  const primaryPath = buildWaitlistPrimaryPath(waitlistTokenSeen)
  const invitePath = ACCESS_GATE_SECONDARY_PATHS.find((path) => path.mode === 'invite')
  const loginPath = ACCESS_GATE_SECONDARY_PATHS.find((path) => path.mode === 'login')

  return (
    <div className="login-card login-card--gate">
      <p className="login-kicker">{gateCopy.kicker}</p>
      <h2>{gateCopy.title}</h2>
      <p className="login-card__body">{gateCopy.body}</p>

      <div className="login-path-list">
        <AccessPathCard
          path={primaryPath}
          tone="primary"
          onClick={() => setView('waitlist')}
          testId="waitlist-cta"
        />
        <div className="login-secondary-row">
          {invitePath && (
            <SecondaryPathButton
              path={invitePath}
              onClick={() => setView('invite')}
              testId="invite-cta"
            />
          )}
          {loginPath && (
            <SecondaryPathButton
              path={loginPath}
              onClick={() => setView('login')}
              testId="login-cta"
            />
          )}
        </div>
      </div>
    </div>
  )
}

export function LoginPage() {
  const rootRef = useRef<HTMLElement | null>(null)
  const [view, setView] = useState<View>('home')
  const [waitlistMe, setWaitlistMe] = useState<WaitlistMe | null>(null)
  const [waitlistTokenSeen, setWaitlistTokenSeen] = useState(false)
  const [waitlistLoading, setWaitlistLoading] = useState(false)

  async function refreshWaitlistStatus(tokenOverride?: string | null) {
    const token = tokenOverride ?? liteSession.getToken()
    if (!token) return
    setWaitlistTokenSeen(true)
    setWaitlistLoading(true)
    try {
      const me = await getMe({ tokenOverride: token })
      setWaitlistMe(me)
    } catch {
      setWaitlistMe(null)
    } finally {
      setWaitlistLoading(false)
    }
  }

  useEffect(() => {
    const token = liteSession.getToken()
    if (token) void refreshWaitlistStatus(token)
    // Mount-only: a cookie token either exists at page load or gets set by
    // WaitlistForm, which then calls refreshWaitlistStatus explicitly.
  }, [])

  useEffect(() => {
    const root = rootRef.current
    if (!root) return
    if (typeof root.scrollTo === 'function') {
      root.scrollTo({ top: 0, behavior: 'auto' })
    } else {
      root.scrollTop = 0
    }
  }, [view])

  const hero = CUIDARI_ACCESS_GATE_COPY.hero

  return (
    <main
      ref={rootRef}
      className="login-root"
      data-testid="cuidari-login"
      aria-labelledby="login-heading"
    >
      <div className="login-shell">
        <section className="login-hero">
          <BrandLockup />
          <div className="login-hero__copy">
            <span className="login-cohort-badge">{hero.badge}</span>
            <h2>{hero.title}</h2>
            <p>{hero.body}</p>
            <div className="login-reason">
              {icon('verified_user')}
              <span>{hero.reason}</span>
            </div>
          </div>
          {view === 'home' && <ReleaseTeaser />}
        </section>

        <section className="login-panel" aria-live="polite">
          {view === 'home' && (
            <GateHome waitlistTokenSeen={waitlistTokenSeen} setView={setView} />
          )}

          {view === 'waitlist' && waitlistTokenSeen && (
            <WaitlistStatusCard
              me={waitlistMe}
              loading={waitlistLoading}
              onRefresh={() => void refreshWaitlistStatus()}
            />
          )}

          {view === 'waitlist' && (
            <WaitlistForm
              onBack={() => setView('home')}
              onJoined={(token) => {
                setWaitlistTokenSeen(Boolean(token) || waitlistTokenSeen)
                if (token) void refreshWaitlistStatus(token)
              }}
            />
          )}

          {view === 'invite' && <InviteForm onBack={() => setView('home')} />}
          {view === 'login' && <LoginForm onBack={() => setView('home')} />}
        </section>
      </div>
    </main>
  )
}
